// Authentication for the console.
//
// There are two kinds of account and they sign in differently. Operators, restaurants and
// drivers use an email and a password. Customers use their phone and a password.
//
// The access token is short-lived and the refresh credential rotates on every use. Both live
// in localStorage: this is a browser console, there is no server-side session to put them
// in, and a token that does not survive a page reload makes the app unusable.

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

const ACCESS_KEY = "30min.accessToken";
const REFRESH_KEY = "30min.refreshToken";
const SESSION_KEY = "30min.session";

export type PrincipalType = "CUSTOMER" | "DRIVER" | "OPERATOR" | "RESTAURANT";

export type Session = {
  id: string;
  principalType: PrincipalType;
  displayName: string;
  maskedPhone?: string;
  roles: string[];
  /**
   * Still signed in on the one-time password an administrator issued: the backend refuses
   * everything but choosing a real password (PASSWORD_CHANGE_REQUIRED) until it is replaced.
   */
  mustChangePassword?: boolean;
};

type Envelope<T> = {
  success: boolean;
  data?: T;
  error?: { code?: string; message?: string };
};

export class AuthError extends Error {
  code: string;
  status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

async function post<T>(
  path: string,
  body: unknown,
  token?: string,
  headers?: Record<string, string>,
): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as Envelope<T>;
  if (!response.ok || !payload.success) {
    throw new AuthError(
      payload.error?.code ?? "REQUEST_FAILED",
      payload.error?.message ?? "Something went wrong. Please try again.",
      response.status,
    );
  }
  return payload.data as T;
}

// ── Stored session ───────────────────────────────────────────────────────────

export function accessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    // Private browsing, or storage disabled. The app still works for one page view.
    return null;
  }
}

export function currentSession(): Session | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function isSignedIn(): boolean {
  return accessToken() !== null;
}

type TokenPair = {
  access_token: string;
  refresh_token: string;
  // Still the administrator-issued one-time password: route to "choose your password" first.
  must_change_password?: boolean;
};
type Principal = {
  id: string;
  principal_type: PrincipalType;
  display_name: string;
  masked_phone?: string;
  roles?: string[];
};

function store(tokens: TokenPair, principal: Principal): Session {
  const session: Session = {
    id: principal.id,
    principalType: principal.principal_type,
    displayName: principal.display_name,
    maskedPhone: principal.masked_phone,
    roles: principal.roles ?? [],
    ...(tokens.must_change_password ? { mustChangePassword: true } : {}),
  };
  try {
    localStorage.setItem(ACCESS_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Nothing to do: the session lives for this page view only.
  }
  notify();
  return session;
}

// Why the last session was ended by the server rather than by the person (30min switched the
// business space off): the sign-in form shows it once.
const SIGN_OUT_REASON_KEY = "30min.signOutReason";

/** Signs out and leaves the reason for the sign-in form (takeSignOutReason). */
export function signOutBecause(reason: string) {
  try {
    sessionStorage.setItem(SIGN_OUT_REASON_KEY, reason);
  } catch {
    // Only the explanation is lost.
  }
  signOut();
}

/** The reason signOutBecause left, once. */
export function takeSignOutReason(): string {
  try {
    const reason = sessionStorage.getItem(SIGN_OUT_REASON_KEY) ?? "";
    sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
    return reason;
  } catch {
    return "";
  }
}

export function signOut() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // Already gone.
  }
  notify();
}

// The access token is short-lived (AUTH_ACCESS_TOKEN_TTL) so that a stolen one is only ever
// good for a few minutes. Nothing signs the user out for that expiry alone: request() calls
// this to trade the long-lived refresh credential for a new pair first, the same way the
// mobile apps do, and only gives up and calls signOut() if that trade itself fails.
//
// The refresh token rotates on every use, so two 401s in flight at once must not each start
// their own refresh — the second would present a credential the first already burned. Every
// caller within one rotation shares this one in-flight request instead.
let refreshInFlight: Promise<string | null> | null = null;

export function refreshAccessToken(): Promise<string | null> {
  if (!refreshInFlight) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<string | null> {
  let refreshToken: string | null;
  try {
    refreshToken = localStorage.getItem(REFRESH_KEY);
  } catch {
    refreshToken = null;
  }
  if (!refreshToken) return null;
  try {
    const data = await post<{ tokens: TokenPair }>("/auth/token/refresh", {
      refresh_token: refreshToken,
    });
    try {
      localStorage.setItem(ACCESS_KEY, data.tokens.access_token);
      localStorage.setItem(REFRESH_KEY, data.tokens.refresh_token);
    } catch {
      // Nothing to do: the session lives for this page view only.
    }
    return data.tokens.access_token;
  } catch {
    // The refresh token is gone, expired, or already rotated out from under us.
    return null;
  }
}

// Components re-read the session when it changes rather than polling it.
const listeners = new Set<() => void>();

export function onSessionChange(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((listener) => listener());
}

// ── Customers: phone and a one-time code ─────────────────────────────────────

export type OtpRequestResult = {
  challenge_sent: boolean;
  expires_in: number;
  masked_phone: string;
  /** Only outside production, so a code can be entered without an SMS gateway. */
  development_code?: string;
};

export function requestCode(phone: string): Promise<OtpRequestResult> {
  return post<OtpRequestResult>("/auth/otp/request", { phone, channel: "customer" });
}

/**
 * Verifies the code and signs the customer in.
 *
 * Passing a name completes a first-time registration in the same call, so somebody signing
 * up does not have to submit twice. It is ignored for an account that already has one.
 */
export async function verifyCode(
  phone: string,
  code: string,
  name?: string,
): Promise<Session> {
  const data = await post<{ tokens: TokenPair; principal: Principal }>(
    "/auth/otp/verify",
    { phone, code, channel: "customer", ...(name ? { name } : {}) },
  );
  return store(data.tokens, data.principal);
}

// ── Operators: email and password ────────────────────────────────────────────

export async function signInCustomer(phone: string, password: string): Promise<Session> {
  const data = await post<{ tokens: TokenPair; principal: Principal }>(
    "/auth/customers/login", { phone, password },
  );
  return store(data.tokens, data.principal);
}

export async function signInOperator(email: string, password: string): Promise<Session> {
  lastPasswordSignIn = { signIn: signInOperator, identifier: email };
  const data = await post<{ tokens: TokenPair; principal: Principal }>(
    "/auth/operators/login",
    { email, password },
  );
  return store(data.tokens, data.principal);
}

// ── Restaurants and drivers: email and an admin-issued password ─────────────

/**
 * A restaurant's own sign-in — its contact phone and the password an administrator generated
 * when the restaurant was created (see ProvisionedRestaurant). A different endpoint from an
 * operator's, because a restaurant is not staff and must never end up holding an operator's
 * authority just for using the same password form. The backend normalises the phone, so
 * "22 123 456" and "+21622123456" both find the same account.
 */
export async function signInRestaurant(phone: string, password: string): Promise<Session> {
  lastPasswordSignIn = { signIn: signInRestaurant, identifier: phone };
  // Says it is the portal: a restaurant whose 30min Pro is switched off is refused here, with
  // the reason as the message the form shows.
  const data = await post<{ tokens: TokenPair; principal: Principal }>(
    "/auth/restaurants/login",
    { phone, password },
    undefined,
    { "X-30min-App": "portal" },
  );
  return store(data.tokens, data.principal);
}

/**
 * A driver's own sign-in — their phone and the password an administrator issued at
 * onboarding. A delivery partner's chief has no phone on the account and types the issued
 * email instead; the backend takes either in the same `phone` field (phone first, then email).
 */
export async function signInDriver(phoneOrEmail: string, password: string): Promise<Session> {
  lastPasswordSignIn = { signIn: signInDriver, identifier: phoneOrEmail };
  const data = await post<{ tokens: TokenPair; principal: Principal }>(
    "/auth/drivers/login",
    { phone: phoneOrEmail, password },
  );
  return store(data.tokens, data.principal);
}

/**
 * Replaces the one-time password the signed-in account was issued with one it chose.
 *
 * The backend revokes every session of the account when its password changes, including this
 * one, so the stored token is dead afterwards: the caller must sign in again with the new
 * password (see signInAgainWith), or sign out.
 */
export async function setOwnPassword(password: string): Promise<void> {
  const token = accessToken();
  if (!token) throw new AuthError("NO_SESSION", "Sign in again to choose your password.", 401);
  await post<{ password_set: boolean }>("/auth/password/set", { password }, token);
}

// Who signed in by password on this page, so that choosing a new password can sign straight
// back in with it. Memory only — never storage — and only the identifier, never a password:
// after a reload it is simply gone and the account signs in again by hand.
let lastPasswordSignIn: {
  signIn: (identifier: string, password: string) => Promise<Session>;
  identifier: string;
} | null = null;

/**
 * Signs in again with a password just chosen, as whoever last signed in on this page.
 * Resolves false when there is nobody to sign in as (the page was reloaded in between).
 */
export async function signInAgainWith(password: string): Promise<boolean> {
  if (!lastPasswordSignIn) return false;
  const { signIn, identifier } = lastPasswordSignIn;
  await signIn(identifier, password);
  return true;
}

export type ForgotResult = {
  sent: boolean;
  message: string;
  /** Only outside production, so a reset can be completed without a mail server. */
  development_reset_token?: string;
};

/**
 * Asks for a reset link.
 *
 * The answer is the same whether or not the address belongs to an account — the screen says
 * so plainly, because a form that confirms which addresses are real is the easiest place to
 * enumerate staff.
 */
export function requestPasswordReset(email: string): Promise<ForgotResult> {
  return post<ForgotResult>("/auth/password/forgot", { email });
}

export function resetPassword(
  email: string,
  token: string,
  password: string,
): Promise<{ reset: boolean }> {
  return post<{ reset: boolean }>("/auth/password/reset", { email, token, password });
}
